
/*
 * Client is the API client.
 */

export default class Client {

  /**
   * Initialize the client.
   */

  constructor({ url, authToken }) {
    this.url = url
    this.authToken = authToken
  }

  // Login creates an auth token and sends a sign-in email.
  async Login({ email, affiliate }) {
    return this._request('login', { email, affiliate })
  }

  // GetTeams returns all the teams you're a member of.
  GetTeams() {
    return this._request('get_teams')
  }

  // GetAccessToken returns an auth token if the code has been verified via email.
  GetAccessToken({ email, code }) {
    return this._request('get_access_token', { email, code })
  }

  // GetTeamMembers returns all the team members in a team.
  GetTeamMembers({ team_id }) {
    return this._request('get_team_members', { team_id })
  }

  // GetTeamInvites returns all the team member invites in a team.
  GetTeamInvites({ team_id }) {
    return this._request('get_team_invites', { team_id })
  }

  // GetTeam returns team information.
  GetTeam({ team_id }) {
    return this._request('get_team', { team_id })
  }

  // UpdateTeam updates team information.
  UpdateTeam({ team }) {
    return this._request('update_team', { team })
  }

  // GetAccount returns user information.
  GetAccount() {
    return this._request('get_account')
  }

  // UpdateAccount updates user information.
  UpdateAccount({ name }) {
    return this._request('update_account', { name })
  }

  // AddTeamMember adds a team invite, or adds the member directly
  // if the user is already present in the database.
  AddTeamMember({ team_id, email, application_name, application_url }) {
    return this._request('add_team_member', { team_id, email, application_name, application_url })
  }

  // RemoveTeamMember removes a team member or invite.
  RemoveTeamMember({ team_id, email }) {
    return this._request('remove_team_member', { team_id, email })
  }

  // AddTeam adds a new team.
  AddTeam({ name, email }) {
    return this._request('add_team', { name, email })
  }

  // RemoveTeam removes a team.
  RemoveTeam({ team_id }) {
    return this._request('remove_team', { team_id })
  }

  // AddSubscription adds a new plan subscription.
  AddSubscription({ team_id, plan_id, coupon }) {
    return this._request('add_subscription', { team_id, plan_id, coupon })
  }

  // GetSubscriptions returns the plans subscribed to.
  GetSubscriptions({ team_id }) {
    return this._request('get_subscriptions', { team_id })
  }

  // UpdateSubscription updates a subscription.
  UpdateSubscription({ team_id, subscription_id, plan_id, coupon }) {
    return this._request('update_subscription', { team_id, subscription_id, plan_id, coupon })
  }

  // RemoveSubscription removes a plan subscription.
  RemoveSubscription({ team_id, subscription_id }) {
    return this._request('remove_subscription', { team_id, subscription_id })
  }

  // GetCreditCard returns credit card information.
  GetCreditCard({ team_id }) {
    return this._request('get_credit_card', { team_id })
  }

  // UpdateCreditCard adds or updates the default credit card.
  UpdateCreditCard({ team_id, token }) {
    return this._request('update_credit_card', { team_id, token })
  }

  // GetProducts returns the products and plans available.
  GetProducts() {
    return this._request('get_products')
  }

  // GetInvoices returns the invoices for all subscriptions.
  GetInvoices({ team_id }) {
    return this._request('get_invoices', { team_id })
  }

  // GetCoupon returns coupon information.
  GetCoupon({ coupon }) {
    return this._request('get_coupon', { coupon })
  }

  // AddFeedback sends feedback from a customer.
  AddFeedback({ message }) {
    return this._request('add_feedback', { message })
  }

  /**
   * Perform a request.
   */

  _request(method, input) {
    return request({
      url: this.url,
      authToken: this.authToken,
      method,
      input
    })
  }
}

/**
 * Request an RPC method.
 */

export async function request({ url, authToken, method, input }) {
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {}
  }

  if (input != null) {
    options.headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(input)
  }

  if (authToken) {
    options.headers['Authorization'] = `Bearer ${authToken}`
  }

  const res = await fetch(`${url}/${method}`, options)
  const type = res.headers.get('Content-Type')

  if (res.status >= 400) {
    if (type == 'application/json') {
      const { type, message } = await res.json()
      const err = new Error(`${type}: ${message}`)
      err.type = type
      err.message = message
      throw err
    } else {
      const err = new Error(`${res.status} ${res.statusText}`)
      err.status = res.status
      throw err
    }
    return
  }

  if (type == 'application/json') {
    return await res.json()
  }
}
